import React, { useState, useEffect, useCallback } from 'react';
import { posService, billingService } from '../../services/dataService';
import InvoicePrint from './InvoicePrint';

const PAYMENT_METHODS = ['cash', 'card', 'upi', 'other'];

const friendlyErr = (err) => {
  if (!err) return 'Something went wrong. Please try again.';
  const msg = String(err.message || err).toLowerCase();
  if (msg.includes('fetch') || msg.includes('network')) return 'Network error — check your internet connection.';
  if (msg.includes('jwt') || msg.includes('expired') || msg.includes('auth')) return 'Session expired — please refresh and log in again.';
  if (msg.includes('policy') || msg.includes('permission') || msg.includes('rls')) return "You don't have permission to do this.";
  if (msg.includes('duplicate') || msg.includes('unique')) return 'This record already exists.';
  if (msg.includes('foreign key') || msg.includes('violates foreign')) return 'This record is in use and cannot be removed.';
  if (msg.includes('timeout') || msg.includes('timed out')) return 'Request timed out — please try again.';
  return 'Something went wrong. Please try again.';
};

const fmtDateTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return (
    d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' · ' +
    d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
  );
};

const DATE_FILTERS = [
  { id: 'today',  label: 'Today' },
  { id: 'week',   label: 'This Week' },
  { id: 'month',  label: 'This Month' },
  { id: 'all',    label: 'All' },
  { id: 'custom', label: 'Custom Range' },
];

const toInputDate = (d) => d.toISOString().slice(0, 10);

const InvoicesApp = ({ restaurant }) => {
  const [orders, setOrders]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [refreshing, setRefreshing]       = useState(false);
  const [search, setSearch]               = useState('');
  const [dateFilter, setDateFilter]       = useState('all');
  const [startDate, setStartDate]         = useState(toInputDate(new Date()));
  const [endDate, setEndDate]             = useState(toInputDate(new Date()));
  const [printOrder, setPrintOrder]       = useState(null);
  const [billingSettings, setBillingSettings] = useState(null);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [pageError, setPageError] = useState('');

  const loadData = useCallback(async (silent = false) => {
    if (!restaurant?.id) return;
    if (!silent) setRefreshing(true);
    const [ordersRes, settingsRes] = await Promise.all([
      posService.getInvoices(restaurant.id),
      billingService.getSettings(restaurant.id),
    ]);
    setOrders(ordersRes.data || []);
    setBillingSettings(settingsRes.data);
    setLoading(false);
    setRefreshing(false);
  }, [restaurant?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const markInvalidFn = async (orderId, invoiceNumber) => {
    if (!window.confirm(`Mark invoice ${invoiceNumber} as invalid? This cannot be undone.`)) return;
    const { error } = await posService.markOrderInvalid(orderId);
    if (error) { setPageError(friendlyErr(error)); return; }
    loadData(true);
  };

  const changePaymentMethod = async (orderId, method) => {
    const { error } = await posService.updatePaymentMethod(orderId, method);
    if (error) { setPageError(friendlyErr(error)); return; }
    setEditingPaymentId(null);
    loadData(true);
  };

  // Separate primary (non-merged) from merged so we can group for print
  const primaryOrders = orders.filter(o => o.status !== 'merged');

  const getMergedOrderNumbers = (order) =>
    orders
      .filter(o => o.invoice_number === order.invoice_number && o.id !== order.id && o.status === 'merged')
      .map(o => o.order_number)
      .filter(Boolean);

  // Date filter
  const now = new Date();
  const dateFiltered = primaryOrders.filter(o => {
    const ref = new Date(o.paid_at || o.billed_at || o.created_at);
    if (dateFilter === 'today') return ref.toDateString() === now.toDateString();
    if (dateFilter === 'week')  { const c = new Date(now); c.setDate(c.getDate() - 7);  return ref >= c; }
    if (dateFilter === 'month') { const c = new Date(now); c.setDate(c.getDate() - 30); return ref >= c; }
    if (dateFilter === 'custom' && startDate && endDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      return ref >= start && ref <= end;
    }
    return true;
  });

  // Text search
  const q = search.trim().toLowerCase();
  const filtered = !q ? dateFiltered : dateFiltered.filter(o => {
    if (o.invoice_number?.toLowerCase().includes(q)) return true;
    if (o.order_number?.toLowerCase().includes(q)) return true;
    const table = o.restaurant_tables
      ? (o.restaurant_tables.custom_name || `table ${o.restaurant_tables.table_number}`)
      : '';
    if (table.toLowerCase().includes(q)) return true;
    if (o.payment_method?.toLowerCase().includes(q)) return true;
    if ((o.order_items || []).some(oi => oi.menu_items?.name?.toLowerCase().includes(q))) return true;
    return false;
  });

  const curr = restaurant?.default_currency || '₹';
  const fmt  = (n) => `${curr}${(n || 0).toFixed(2)}`;

  // Summary counts for date-filtered set
  const validFiltered  = filtered.filter(o => !o.is_invalid);
  const totalCollected = validFiltered.reduce((s, o) => s + (o.total_payable || o.total_amount || 0), 0);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '20px', textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid #FF4444', borderTop: '4px solid transparent', borderRadius: '50%', margin: '0 auto 20px', animation: 'spin 1s linear infinite' }} />
          <h2 style={{ color: '#333', margin: 0 }}>Loading Invoices...</h2>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px 20px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: '0 0 4px 0', fontSize: '1.8rem', fontWeight: '700', background: 'linear-gradient(135deg, #FF4444, #FF6B00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Invoices
          </h1>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
            {dateFilter === 'custom' && startDate && endDate
              ? `${startDate} → ${endDate}`
              : 'Last 6 months'
            } · {filtered.length} invoice{filtered.length !== 1 ? 's' : ''}
            {filtered.length > 0 && ` · ${fmt(totalCollected)} collected`}
          </p>
        </div>
        <button
          onClick={() => loadData()}
          disabled={refreshing}
          style={{
            background: refreshing ? '#e5e7eb' : 'linear-gradient(135deg, #FF4444, #FF6B00)',
            color: refreshing ? '#9ca3af' : 'white',
            border: 'none', borderRadius: '10px',
            padding: '10px 20px', fontSize: '0.9rem', fontWeight: '600',
            cursor: refreshing ? 'not-allowed' : 'pointer',
            boxShadow: refreshing ? 'none' : '0 3px 10px rgba(255,68,68,0.25)',
          }}
        >
          {refreshing ? 'Refreshing...' : '↻ Refresh'}
        </button>
      </div>

      {/* Error banner */}
      {pageError && (
        <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: '12px', padding: '14px 18px', marginBottom: '20px', color: '#dc2626', fontWeight: '600', fontSize: '0.92rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <span>{pageError}</span>
          <button onClick={() => setPageError('')} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1.1rem', padding: '0', lineHeight: 1 }}>&#x2715;</button>
        </div>
      )}

      {/* Search */}
      <input
        type="text"
        placeholder="Search by invoice #, order #, table, dish name, payment method..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{
          width: '100%', padding: '11px 16px', borderRadius: '12px',
          border: '1.5px solid #e5e7eb', fontSize: '0.92rem',
          outline: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          fontFamily: 'inherit', marginBottom: '12px', boxSizing: 'border-box',
        }}
      />

      {/* Date filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '6px', display: 'flex', gap: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', flexShrink: 0 }}>
          {DATE_FILTERS.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setDateFilter(id)}
              style={{
                padding: '7px 14px', border: 'none', borderRadius: '8px',
                fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer',
                background: dateFilter === id ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : 'transparent',
                color: dateFilter === id ? 'white' : '#666',
                transition: 'all 0.2s', whiteSpace: 'nowrap',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Custom date range inputs */}
        {dateFilter === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'white', borderRadius: '12px', padding: '8px 14px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: '600', color: '#666', whiteSpace: 'nowrap' }}>From</span>
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={e => setStartDate(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontSize: '0.88rem', outline: 'none', fontFamily: 'inherit', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.82rem', fontWeight: '600', color: '#666' }}>To</span>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={toInputDate(new Date())}
              onChange={e => setEndDate(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontSize: '0.88rem', outline: 'none', fontFamily: 'inherit', cursor: 'pointer' }}
            />
          </div>
        )}
      </div>

      {/* Empty state */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 40px', background: 'white', borderRadius: '20px', boxShadow: '0 10px 40px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🧾</div>
          <h3 style={{ color: '#333', margin: '0 0 8px 0', fontSize: '1.4rem', fontWeight: '600' }}>No invoices found</h3>
          <p style={{ color: '#666', margin: 0 }}>
            {search ? 'Try a different search term' : 'No invoices for the selected period'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '18px' }}>
          {filtered.map(order => {
            const isInvalid = !!order.is_invalid;
            const mergedNums = getMergedOrderNumbers(order);
            const isEditing = editingPaymentId === order.id;

            return (
              <div
                key={order.id}
                style={{
                  background: 'white', borderRadius: '15px', padding: '20px',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
                  border: '1px solid rgba(0,0,0,0.06)',
                  borderLeft: `4px solid ${isInvalid ? '#dc2626' : '#16a34a'}`,
                  opacity: isInvalid ? 0.75 : 1,
                }}
              >
                {/* Top row: invoice # + total */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                      <span style={{ fontWeight: '800', fontSize: '1.05rem', color: isInvalid ? '#999' : '#333', textDecoration: isInvalid ? 'line-through' : 'none' }}>
                        {order.invoice_number}
                      </span>
                      {isInvalid && (
                        <span style={{ background: '#fee2e2', color: '#991b1b', fontSize: '0.65rem', fontWeight: '700', padding: '2px 6px', borderRadius: '4px' }}>
                          INVALID
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: '#666' }}>
                      {order.order_number}
                      {order.restaurant_tables && (
                        <span> · {order.restaurant_tables.custom_name || `Table ${order.restaurant_tables.table_number}`}</span>
                      )}
                    </div>
                    {mergedNums.length > 0 && (
                      <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '2px' }}>
                        Merged: {mergedNums.join(', ')}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '12px' }}>
                    <div style={{
                      fontSize: '1.3rem', fontWeight: '800',
                      background: isInvalid ? 'none' : 'linear-gradient(135deg, #FF4444, #FF6B00)',
                      WebkitBackgroundClip: isInvalid ? 'unset' : 'text',
                      WebkitTextFillColor: isInvalid ? '#aaa' : 'transparent',
                      color: isInvalid ? '#aaa' : undefined,
                    }}>
                      {fmt(order.total_payable || order.total_amount)}
                    </div>
                    {order.discount_amount > 0 && (
                      <div style={{ fontSize: '0.72rem', color: '#16a34a' }}>−{fmt(order.discount_amount)} off</div>
                    )}
                  </div>
                </div>

                {/* Badges */}
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  {order.payment_method && (
                    <span style={{ padding: '2px 10px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '700', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>
                      {order.payment_method.toUpperCase()}
                    </span>
                  )}
                  <span style={{
                    padding: '2px 10px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '600',
                    background: order.paid_at ? '#f0fdf4' : '#fef3c7',
                    color: order.paid_at ? '#16a34a' : '#92400e',
                    border: `1px solid ${order.paid_at ? '#bbf7d0' : '#fcd34d'}`,
                  }}>
                    {order.paid_at ? 'Paid' : 'Unpaid'}
                  </span>
                  {order.overridden_at && (
                    <span style={{ padding: '2px 10px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '600', background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}>
                      ⚠️ Override
                    </span>
                  )}
                </div>

                {/* Date/time */}
                <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '12px' }}>
                  {order.paid_at
                    ? `Paid: ${fmtDateTime(order.paid_at)}`
                    : `Billed: ${fmtDateTime(order.billed_at || order.created_at)}`}
                </div>

                {/* Order items */}
                <div style={{ background: '#f8f9fa', borderRadius: '10px', padding: '12px', marginBottom: '12px' }}>
                  {(order.order_items || []).slice(0, 5).map(oi => (
                    <div key={oi.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '5px', color: '#555' }}>
                      <span>{oi.quantity}× {oi.menu_items?.name}</span>
                      <span style={{ fontWeight: '600', color: '#FF6B00' }}>
                        {fmt((oi.unit_price || 0) * oi.quantity)}
                      </span>
                    </div>
                  ))}
                  {(order.order_items || []).length > 5 && (
                    <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '4px' }}>
                      +{order.order_items.length - 5} more items
                    </div>
                  )}
                </div>

                {/* Edit payment method inline */}
                {!isInvalid && isEditing && (
                  <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                    {PAYMENT_METHODS.map(m => (
                      <button
                        key={m}
                        onClick={() => changePaymentMethod(order.id, m)}
                        style={{
                          flex: 1, padding: '7px 4px', borderRadius: '7px', fontSize: '0.78rem',
                          fontWeight: '700', cursor: 'pointer', border: 'none',
                          background: order.payment_method === m
                            ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : '#f3f4f6',
                          color: order.payment_method === m ? 'white' : '#555',
                        }}
                      >
                        {m.toUpperCase()}
                      </button>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setPrintOrder({ order, mergedOrderNumbers: mergedNums })}
                    style={{
                      flex: 1, padding: '9px 8px', borderRadius: '9px', fontSize: '0.85rem', fontWeight: '700',
                      cursor: 'pointer', background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
                      color: 'white', border: 'none', boxShadow: '0 3px 8px rgba(255,68,68,0.25)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    🖨 Reprint
                  </button>
                  {!isInvalid && (
                    <button
                      onClick={() => setEditingPaymentId(isEditing ? null : order.id)}
                      style={{
                        flex: 1, padding: '9px 8px', borderRadius: '9px', fontSize: '0.85rem',
                        fontWeight: '700', cursor: 'pointer', background: 'transparent',
                        color: '#555', border: '1.5px solid #e5e7eb', whiteSpace: 'nowrap',
                      }}
                    >
                      Edit Payment
                    </button>
                  )}
                  {!isInvalid && (
                    <button
                      onClick={() => markInvalidFn(order.id, order.invoice_number)}
                      style={{
                        flex: 1, padding: '9px 8px', borderRadius: '9px', fontSize: '0.85rem',
                        fontWeight: '700', cursor: 'pointer', background: 'transparent',
                        color: '#dc2626', border: '1.5px solid #fca5a5', whiteSpace: 'nowrap',
                      }}
                    >
                      Mark Invalid
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {printOrder && (
        <InvoicePrint
          order={printOrder.order}
          mergedOrderNumbers={printOrder.mergedOrderNumbers}
          restaurant={restaurant}
          billingSettings={billingSettings}
          onClose={() => setPrintOrder(null)}
        />
      )}
    </div>
  );
};

export default InvoicesApp;
